import { useState, useEffect } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import { formatDateDDMMYY } from '../../utils/poExpenseHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shows the last 2 real Zoho CRM Notes for a linked Account or Project, and
 * lets you add a new one — which is written straight to that CRM record.
 * kind: 'account' | 'project'. zohoId: the linked Zoho record id (Accounts / Potentials).
 */
export const SpellersNotesCell = ({ zohoId, kind, testId, initialNote = null }) => {
  // The newest CRM note stored on the row (refreshed from the CRM in the background) — shown straight away, without clicking.
  const [latest, setLatest] = useState(initialNote);
  useEffect(() => { setLatest(initialNote); }, [initialNote]);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = kind === 'account'
        ? `${API}/zoho/account/${zohoId}/notes?limit=2`
        : `${API}/zoho/project/${zohoId}/notes?limit=2`;
      const res = await api.get(url);
      setNotes(res.data.notes || []);
      setLatest((res.data.notes || [])[0]?.content || null);
    } catch (e) {
      toast.error('Failed to load notes');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && notes === null) load();
  };

  const addNote = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      if (kind === 'account') {
        await api.post(`${API}/zoho/create-note`, { account_id: zohoId, note_content: draft.trim() });
      } else {
        await api.post(`${API}/zoho/project/${zohoId}/notes`, { note_content: draft.trim() });
      }
      setDraft('');
      await load();
      toast.success('Note added to CRM');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  if (!zohoId) {
    return <span className="text-xs text-gray-400 italic" data-testid={testId ? `${testId}-no-crm` : undefined}>Not in CRM</span>;
  }

  return (
    <div className="relative" data-testid={testId}>
      <button
        onClick={handleToggle}
        className="flex items-center gap-1 text-xs text-gray-600 hover:text-orange-600 text-left max-w-[160px] truncate"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <MessageSquarePlus className="w-3 h-3 shrink-0" />
        {notes && notes[0] ? notes[0].content : (latest || 'Add note…')}
      </button>
      {open && (
        <div
          className="absolute z-30 top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-lg p-3 space-y-2"
          data-testid={testId ? `${testId}-popover` : undefined}
        >
          {loading ? (
            <div className="text-xs text-gray-400">Loading…</div>
          ) : (notes || []).length === 0 ? (
            <div className="text-xs text-gray-400">No notes yet</div>
          ) : (
            notes.slice(0, 2).map((n) => (
              <div key={n.id} className="text-xs border-b border-gray-100 pb-1.5">
                <div className="text-gray-800">{n.content}</div>
                <div className="text-gray-400 mt-0.5">{[n.owner_name, n.created_at ? formatDateDDMMYY(n.created_at) : null].filter(Boolean).join(' · ')}</div>
              </div>
            ))
          )}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="Add a note…"
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="text-xs text-gray-400 px-2" data-testid={testId ? `${testId}-close` : undefined}>Close</button>
            <button
              onClick={addNote}
              disabled={saving}
              className="text-xs font-semibold text-white px-3 py-1 rounded-lg disabled:opacity-60"
              style={{ backgroundColor: '#ee7203' }}
              data-testid={testId ? `${testId}-save` : undefined}
            >
              {saving ? 'Saving…' : 'Add'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpellersNotesCell;
